import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from "@microsoft/signalr";
import { makeAutoObservable, runInAction, reaction, observable } from "mobx";
import { store } from "./store";
import { agent } from "../api/agent";

export interface MessageDTO {
    id: string;
    conversationId: string;
    senderId: string;
    recipientId?: string;
    senderFullName: string;
    senderImage: string;
    content: string;
    createdAt: Date;
    isRead: boolean;
    isSentByCurrentUser: boolean;
}

export interface ConversationDTO {
    id: string;
    otherUserId: string;
    otherUserFullName: string;
    otherUserImage: string;
    otherUserUserName: string;
    isOtherUserOnline: boolean;
    lastMessage: string;
    lastMessageAt: Date;
    unreadCount: number;
    gameId?: string;
    gameTitle?: string;
    gameDate?: Date;
}

export interface ConversationDetailDTO {
    id: string;
    otherUserId: string;
    otherUserFullName: string;
    otherUserImage: string;
    otherUserUserName: string;
    isOtherUserOnline: boolean;
    gameId?: string;
    gameTitle?: string;
    gameDate?: Date;
}

export default class MessageStore {
    conversations: ConversationDTO[] = [];
    messages: MessageDTO[] = [];
    selectedConversation: ConversationDetailDTO | null = null;
    hubConnection: HubConnection | null = null;
    
    // 🔥 FIXED: Use observable.map instead of Set for MobX reactivity
    onlineUsers: Map<string, boolean> = observable.map<string, boolean>();
    
    // Private state management
    private presenceInterval: NodeJS.Timeout | null = null;
    private desktopRefreshInterval: NodeJS.Timeout | null = null;
    private keepAliveInterval: NodeJS.Timeout | null = null;
    private hasJoinedDesktopGroups: boolean = false;
    private isSelectingConversation: boolean = false;
    
    // Connection management
    private isConnecting: boolean = false;
    private connectionRetryCount: number = 0;
    private readonly maxConnectionRetries: number = 5;
    private connectionRetryTimeout: NodeJS.Timeout | null = null;
    private desktopFallbackInterval: NodeJS.Timeout | null = null;
    
    // Online status refresh interval
    private onlineStatusRefreshInterval: NodeJS.Timeout | null = null;
    
    // Public loading states
    loading: boolean = false;
    loadingMessages: boolean = false;

    constructor() {
        makeAutoObservable(this, {
            selectedConversation: true,
            conversations: true,
            messages: true,
            loadingMessages: true
        });

        this.setupReactions();
        this.setupPageVisibilityListener();
    }

    private setupReactions = () => {
        // Monitor conversation selection changes
        reaction(
            () => this.selectedConversation?.id,
            (selectedId) => {
                console.log('🔵 MobX Reaction: selectedConversation changed to:', selectedId);
            }
        );

        // Monitor connection state changes
        reaction(
            () => this.hubConnection?.state,
            (state) => {
                console.log('🔌 Hub connection state changed:', state);
                this.handleConnectionStateChange(state);
            }
        );
    };

    // Page visibility detection
    private setupPageVisibilityListener = () => {
        if (typeof window === 'undefined') return;
        
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                // Tab is hidden - send a final presence update
                console.log('📄 Tab hidden, sending final presence');
                this.sendFinalPresence();
            } else {
                // Tab is visible again - refresh presence
                console.log('📄 Tab visible, refreshing presence');
                setTimeout(() => {
                    this.refreshPresence();
                }, 1000);
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Also listen for page unload
        window.addEventListener('beforeunload', () => {
            this.sendFinalPresence();
        });
    };

    private sendFinalPresence = () => {
        if (this.hubConnection?.state === 'Connected') {
            // Try to send one last presence update
            this.hubConnection.invoke('UpdatePresence')
                .then(() => console.log('✅ Final presence sent'))
                .catch(() => { /* Ignore */ });
        }
    };

    private refreshPresence = () => {
        if (this.hubConnection?.state === 'Connected') {
            this.hubConnection.invoke('UpdatePresence')
                .then(() => console.log('✅ Presence refreshed after visibility change'))
                .catch(() => { /* Ignore */ });
        }
    };

    private handleConnectionStateChange = (state: string | undefined) => {
        if (state === 'Connected') {
            // Store references to avoid race conditions
            const selectedConv = this.selectedConversation;
            const hubConnection = this.hubConnection;
            
            if (selectedConv?.id && 
                selectedConv.id !== '00000000-0000-0000-0000-000000000000' &&
                hubConnection) {
                
                // Re-join selected conversation after reconnection
                setTimeout(async () => {
                    try {
                        if (hubConnection.state === 'Connected') {
                            await this.safeHubInvoke('JoinConversation', selectedConv.id);
                            console.log('✅ Rejoined conversation after reconnect');
                        }
                    } catch (error) {
                        console.error('Error rejoining conversation:', error);
                    }
                }, 500);
            }
            
            // Re-setup desktop mode
            this.checkAndSetupDesktopMode();
        }
    };

    // ========== PUBLIC GETTERS ==========
    get totalUnreadCount(): number {
        return this.conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
    }

    get selectedConversationId(): string | null {
        return this.selectedConversation?.id || null;
    }

    // ========== HUB CONNECTION MANAGEMENT ==========
    createHubConnection = async (isRetry: boolean = false): Promise<void> => {
        // Prevent multiple simultaneous connection attempts
        if (this.isConnecting) {
            console.log('⚠️ Already connecting, skipping duplicate request');
            return;
        }

        const token = store.userStore.user?.token;
        if (!token) {
            console.log('❌ No token available, skipping hub connection');
            return;
        }

        // Cancel any pending retry
        this.cancelPendingRetry();

        this.isConnecting = true;
        console.log('🌐 Creating hub connection. Is retry:', isRetry, 'Browser:', navigator.userAgent);

        // Clean up existing connection
        await this.cleanupExistingConnection();

        // Reset retry count on successful page load (not retry)
        if (!isRetry) {
            this.connectionRetryCount = 0;
        }

        // Build new connection
        this.buildHubConnection(token);

        try {
            console.log('🚀 Starting connection...');
            await this.hubConnection!.start();
            
            console.log('✅ Hub connected successfully');
            console.log('📊 Connection details:', {
                connectionId: this.hubConnection?.connectionId?.substring(0, 8)
            });
            
            this.onConnectionSuccess();
            
        } catch (error) {
            await this.handleConnectionFailure(error, isRetry);
        }
    };

    private buildHubConnection = (token: string) => {
        const hubUrl = process.env.NODE_ENV === 'production' 
        ? '/hubs/messages' 
        : 'http://localhost:5000/hubs/messages';
        this.hubConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => token,
                skipNegotiation: false,
                transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    console.log(`🔁 Automatic reconnect attempt ${retryContext.previousRetryCount}`);
                    // Quick retry for first attempts, then exponential backoff
                    if (retryContext.previousRetryCount < 2) return 1000;
                    return Math.min(2000 * Math.pow(1.5, retryContext.previousRetryCount), 30000);
                }
            })
            .configureLogging(LogLevel.Warning)
            .build();

        this.setupHubEventHandlers();
    };

    private setupHubEventHandlers = () => {
        if (!this.hubConnection) return;

        this.hubConnection.onclose((error) => {
            console.log('🔴 Connection closed:', error?.message || 'Normal closure');
            this.isConnecting = false;
            
            // Reset retry count on clean closure
            if (!error) {
                this.connectionRetryCount = 0;
            }
        });

        this.hubConnection.onreconnecting((error) => {
            console.log('🔄 Reconnecting:', error?.message || 'No error');
        });

        this.hubConnection.onreconnected(async (connectionId) => {
            console.log('✅ Reconnected with ID:', connectionId);
            this.isConnecting = false;
            this.connectionRetryCount = 0;
            
            // Update presence after reconnect
            setTimeout(async () => {
                try {
                    await this.hubConnection?.invoke('UpdatePresence');
                    console.log('✅ Presence updated after reconnect');
                } catch (err) {
                    console.error('Error sending presence after reconnect:', err);
                }
            }, 300);
        });

        // Application event handlers
        this.hubConnection.on('LoadConversations', this.handleLoadConversations);
        this.hubConnection.on('ReceiveMessage', this.handleReceiveMessage);
        this.hubConnection.on('ConversationUpdated', this.handleConversationUpdated);
        this.hubConnection.on('JoinNewConversation', this.handleJoinNewConversation);
        this.hubConnection.on('UserOnlineStatusChanged', this.handleUserOnlineStatusChanged);
        this.hubConnection.on('MessageError', this.handleMessageError);
        this.hubConnection.on('RateLimitExceeded', this.handleRateLimitExceeded);
        this.hubConnection.on('JoinedConversation', this.handleJoinedConversation);
        this.hubConnection.on('LeftConversation', this.handleLeftConversation);
        this.hubConnection.on('LoadOnlineUsers', this.handleLoadOnlineUsers);
        this.hubConnection.on('MessageDeleted', this.handleMessageDeleted);
    };

    private onConnectionSuccess = () => {
        this.isConnecting = false;
        this.connectionRetryCount = 0;
        
        // Start presence updates with more frequent heartbeats
        this.startPresenceHeartbeat();
        
        // Start online status refresh
        this.startOnlineStatusRefresh();
        
        // Send initial presence
        setTimeout(() => {
            this.hubConnection?.invoke('UpdatePresence')
                .then(() => console.log('✅ Presence updated'))
                .catch(err => console.error('❌ Presence failed:', err));
            
            // Setup desktop mode if applicable
            this.checkAndSetupDesktopMode();
        }, 300);
    };

    private handleMessageDeleted = (messageId: string) => {
        runInAction(() => {
            this.messages = this.messages.filter(m => m.id !== messageId);
        });
    };

    // More frequent presence heartbeat
    private startPresenceHeartbeat = () => {
        // Clear existing interval
        if (this.presenceInterval) {
            clearInterval(this.presenceInterval);
        }
        
        // Increased frequency: every 15 seconds (server expires at 45 seconds)
        this.presenceInterval = setInterval(() => {
            if (this.hubConnection?.state === 'Connected') {
                this.hubConnection.invoke('UpdatePresence')
                    .then(() => console.log('✅ Presence heartbeat sent'))
                    .catch(err => console.error('❌ Presence heartbeat failed:', err));
            }
        }, 15000);
        
        // Also send an immediate presence update
        setTimeout(() => {
            if (this.hubConnection?.state === 'Connected') {
                this.hubConnection.invoke('UpdatePresence')
                    .catch(() => { /* Ignore */ });
            }
        }, 1000);
    };

    // Start online status refresh interval
    private startOnlineStatusRefresh = () => {
        // Clear existing interval
        if (this.onlineStatusRefreshInterval) {
            clearInterval(this.onlineStatusRefreshInterval);
        }
        
        // Refresh online users every 30 seconds
        this.onlineStatusRefreshInterval = setInterval(async () => {
            if (this.hubConnection?.state === 'Connected') {
                try {
                    await this.refreshOnlineUsers();
                } catch (error) {
                    console.error('Error refreshing online users:', error);
                }
            }
        }, 30000);
        
        // Initial refresh after 5 seconds
        setTimeout(() => {
            if (this.hubConnection?.state === 'Connected') {
                this.refreshOnlineUsers();
            }
        }, 5000);
    };

    // Stop online status refresh
    private stopOnlineStatusRefresh = () => {
        if (this.onlineStatusRefreshInterval) {
            clearInterval(this.onlineStatusRefreshInterval);
            this.onlineStatusRefreshInterval = null;
        }
    };

    // Refresh online users
    refreshOnlineUsers = async (): Promise<void> => {
        if (!this.hubConnection || this.hubConnection.state !== 'Connected') {
            return;
        }
        
        try {
            console.log('🔄 Refreshing online users list');
            // Server will push updates via UserOnlineStatusChanged events
            // This is just a placeholder for any future online users refresh logic
        } catch (error) {
            console.error('Error refreshing online users:', error);
        }
    };

    private cleanupExistingConnection = async (): Promise<void> => {
        if (this.hubConnection && this.hubConnection.state !== 'Disconnected') {
            console.log('🛑 Stopping existing connection first');
            try {
                // Don't wait too long for stop to complete
                const stopPromise = this.hubConnection.stop();
                await Promise.race([
                    stopPromise,
                    new Promise(resolve => setTimeout(resolve, 500))
                ]);
            } catch (error) {
                // Ignore errors during stop
            }
        }
    };

    private cancelPendingRetry = () => {
        if (this.connectionRetryTimeout) {
            clearTimeout(this.connectionRetryTimeout);
            this.connectionRetryTimeout = null;
        }
    };

    private handleConnectionFailure = async (error: any, isRetry: boolean) => {
        this.isConnecting = false;
        
        console.error('❌ Connection failed:', {
            name: error.name,
            message: error.message
        });
        
        this.connectionRetryCount++;
        
        if (this.connectionRetryCount >= this.maxConnectionRetries) {
            console.log('🚫 Max connection retries reached, giving up');
            this.connectionRetryCount = 0;
            return;
        }
        
        // Exponential backoff with jitter
        const baseDelay = 2000;
        const maxDelay = 30000;
        const delay = Math.min(
            baseDelay * Math.pow(1.5, this.connectionRetryCount - 1),
            maxDelay
        );
        
        // Add jitter (±20%) to prevent thundering herd
        const jitter = delay * 0.2;
        const finalDelay = delay + (Math.random() * jitter * 2 - jitter);
        
        console.log(`🔄 Retrying in ${Math.round(finalDelay)}ms... (attempt ${this.connectionRetryCount})`);
        
        this.connectionRetryTimeout = setTimeout(() => {
            this.createHubConnection(true);
        }, finalDelay);
    };

    // ========== EVENT HANDLERS ==========
    private handleLoadConversations = (conversations: ConversationDTO[]) => {
        console.log('LoadConversations event received, count:', conversations.length);

        runInAction(() => {
            conversations.forEach(conv => {
                if (conv.lastMessageAt) {
                    conv.lastMessageAt = new Date(conv.lastMessageAt);
                }
                if (conv.gameDate) {
                    conv.gameDate = new Date(conv.gameDate);
                }
            });
            this.conversations = conversations;
            console.log('Conversations stored in messageStore:', this.conversations.length);
        });
    };

    // 🔥 FIXED: Handle initial online users load
    private handleLoadOnlineUsers = (userIds: string[]) => {
        runInAction(() => {
            console.log(`📊 Loading ${userIds.length} online users`);
            
            // Clear and populate the Map
            this.onlineUsers.clear();
            userIds.forEach(userId => this.onlineUsers.set(userId, true));
            
            // Update all conversations
            this.conversations.forEach(conv => {
                conv.isOtherUserOnline = this.onlineUsers.has(conv.otherUserId);
            });
            
            if (this.selectedConversation) {
                this.selectedConversation.isOtherUserOnline = 
                    this.onlineUsers.has(this.selectedConversation.otherUserId);
            }
        });
    };

    // 🔥 FIXED: Enhanced user online status handler
    private handleUserOnlineStatusChanged = (userId: string, isOnline: boolean) => {
        console.log(`🌍 User ${userId} is now ${isOnline ? 'online' : 'offline'} (global)`);
        
        runInAction(() => {
            // Use Map methods for MobX reactivity
            if (isOnline) {
                this.onlineUsers.set(userId, true);
            } else {
                this.onlineUsers.delete(userId);
            }

            // Update conversation online status
            this.conversations.forEach(conv => {
                if (conv.otherUserId === userId) {
                    conv.isOtherUserOnline = isOnline;
                }
            });

            if (this.selectedConversation && this.selectedConversation.otherUserId === userId) {
                this.selectedConversation.isOtherUserOnline = isOnline;
            }
        });
    };

    private handleReceiveMessage = (message: MessageDTO) => {
        console.log('📨 Immediate message received at:', new Date().toISOString());
    
        runInAction(() => {
            message.createdAt = new Date(message.createdAt);
            
            // Fix isSentByCurrentUser
            const currentUserId = store.userStore.user?.userID;
            if (currentUserId) {
                message.isSentByCurrentUser = message.senderId === currentUserId;
            }
    
            const isForCurrentConversation = this.selectedConversation && (
                message.conversationId === this.selectedConversation.id ||
                (this.selectedConversation.id === '00000000-0000-0000-0000-000000000000' &&
                 (message.isSentByCurrentUser ? message.recipientId : message.senderId) === 
                 this.selectedConversation.otherUserId)
            );
    
            if (isForCurrentConversation) {
                const messageExists = this.messages.some(m => m.id === message.id);
                if (!messageExists) {
                    this.messages.push(message);
                }
    
                if (!message.isSentByCurrentUser) {
                    this.markConversationAsRead(message.conversationId);
                }
            } else {
                if (!message.isSentByCurrentUser) {
                    // Update unread count immediately via state
                    this.updateConversationUnreadCount(message.conversationId, true);
                    
                    // REMOVED: No more forced API refresh
                    // The state update above + updateConversationInList below is sufficient
                }
            }
    
            // Update conversation in list (already does this properly)
            this.updateConversationInList(message);
        });
    };

    private handleConversationUpdated = (conversationId: string) => {
        console.log('🔄 ConversationUpdated event:', conversationId);
        
        // Only refresh if this specific conversation isn't already in our list
        // SignalR should handle most updates via other events
        runInAction(() => {
            const exists = this.conversations.some(c => c.id === conversationId);
            if (!exists) {
                console.log('New conversation detected, refreshing list');
                setTimeout(() => {
                    this.refreshConversations();
                }, 100);
            } else {
                console.log('Conversation exists, trusting SignalR events for updates');
            }
        });
    };

    private handleJoinNewConversation = async (conversationId: string) => {
        console.log('JoinNewConversation event received for:', conversationId);

        try {
            await this.safeHubInvoke('JoinConversation', conversationId);
            await this.refreshConversations();
            console.log('Successfully joined new conversation:', conversationId);
        } catch (error) {
            console.error('Error joining new conversation:', error);
        }
    };

    private handleMessageError = (error: string) => {
        console.error('Message error:', error);
    };

    private handleRateLimitExceeded = (message: string) => {
        console.warn('Rate limit:', message);
    };

    private handleJoinedConversation = (conversationId: string) => {
        console.log('✅ Confirmed joined conversation:', conversationId);
    };

    private handleLeftConversation = (conversationId: string) => {
        console.log('✅ Confirmed left conversation:', conversationId);
    };

    // ========== CONNECTION UTILITIES ==========
    ensureConnectionReady = async (): Promise<boolean> => {
        const hubConnection = this.hubConnection;
        
        if (!hubConnection) {
            console.log('🔌 No hub connection instance');
            return await this.createNewConnectionIfPossible();
        }

        if (hubConnection.state === 'Connected') {
            return true;
        }

        console.log('🔌 Connection not ready, state:', hubConnection.state);

        // If we're already trying to connect, wait for that to complete
        if (this.isConnecting) {
            return await this.waitForConnection(hubConnection, 5000);
        }

        if (hubConnection.state === 'Disconnected') {
            console.log('🔄 Attempting to restart connection...');
            try {
                await this.createHubConnection(true);
                
                // Wait for connection to stabilize
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                return this.hubConnection?.state === 'Connected' || false;
            } catch (error) {
                console.error('❌ Failed to restart connection:', error);
                return false;
            }
        }

        // If connecting or reconnecting, wait for it
        if (hubConnection.state === 'Connecting' || hubConnection.state === 'Reconnecting') {
            return await this.waitForConnectionState(hubConnection, ['Connected'], 3000);
        }

        return false;
    };

    private createNewConnectionIfPossible = async (): Promise<boolean> => {
        if (store.userStore.user?.token) {
            console.log('🔄 Creating new hub connection...');
            await this.createHubConnection();
            
            // Wait for connection attempt
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            return this.hubConnection?.state === 'Connected' || false;
        }
        return false;
    };

    private waitForConnection = async (hubConnection: HubConnection, maxWait: number): Promise<boolean> => {
        console.log('⏳ Already connecting, waiting...');
        let waitTime = 0;
        
        while (waitTime < maxWait) {
            await new Promise(resolve => setTimeout(resolve, 500));
            waitTime += 500;
            
            if (hubConnection.state === 'Connected') {
                console.log('✅ Connection ready after waiting');
                return true;
            }
            
            if (hubConnection.state === 'Disconnected' && !this.isConnecting) {
                break;
            }
        }
        
        console.log('⚠️ Connection attempt timed out');
        return false;
    };

    private waitForConnectionState = async (
        hubConnection: HubConnection, 
        targetStates: string[], 
        maxWait: number
    ): Promise<boolean> => {
        console.log(`⏳ Connection is ${hubConnection.state}, waiting for ${targetStates.join(' or ')}...`);
        
        let waitTime = 0;
        const startTime = Date.now();
        
        while (Date.now() - startTime < maxWait) {
            if (targetStates.includes(hubConnection.state)) {
                console.log(`✅ Connection reached target state: ${hubConnection.state}`);
                return true;
            }
            
            if (hubConnection.state === 'Disconnected') {
                console.log(`❌ Connection failed`);
                break;
            }
            
            await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        console.log(`⚠️ Connection state wait timed out`);
        return false;
    };

    safeHubInvoke = async <T>(methodName: string, ...args: any[]): Promise<T | null> => {
        const maxRetries = 2;
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                if (!this.hubConnection) {
                    throw new Error('Hub connection not initialized');
                }
                
                // Ensure connection is ready
                if (this.hubConnection.state !== 'Connected') {
                    const isReady = await this.ensureConnectionReady();
                    if (!isReady) {
                        throw new Error('Connection not ready');
                    }
                }
                
                return await this.hubConnection.invoke<T>(methodName, ...args);
            } catch (error) {
                console.error(`❌ Hub invoke failed (attempt ${attempt + 1}):`, error);
                
                if (attempt === maxRetries) {
                    throw error;
                }
                
                // Wait before retry with exponential backoff
                await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
            }
        }
        
        return null;
    };

    // ========== CONVERSATION MANAGEMENT ==========
    selectConversation = async (conversationId: string, otherUserId?: string) => {
        if (this.isSelectingConversation) {
            console.log('⚠️ Already selecting a conversation, ignoring request');
            return;
        }
    
        // 🔥 FIX: For new conversations, also check otherUserId to differentiate between different friends
        const isSameConversation = conversationId !== '00000000-0000-0000-0000-000000000000'
            ? this.selectedConversation?.id === conversationId
            : (this.selectedConversation?.id === conversationId && 
               this.selectedConversation?.otherUserId === otherUserId);
    
        if (isSameConversation) {
            console.log('ℹ️ Conversation already selected:', conversationId, 'otherUserId:', otherUserId);
            return;
        }
    
        try {
            console.log('=== selectConversation START ===');
            console.log('Target conversation ID:', conversationId);
            console.log('Target other user ID:', otherUserId);
    
            runInAction(() => {
                this.isSelectingConversation = true;
                this.loadingMessages = true;
            });
    
            // Ensure connection is ready
            const isConnectionReady = await this.ensureConnectionReady();
            if (!isConnectionReady) {
                console.error('❌ Cannot select conversation - hub connection not ready');
                throw new Error('Connection not ready. Please try again.');
            }
    
            // Leave previous conversation
            await this.leavePreviousConversation();
    
            const isNewConversation = conversationId === '00000000-0000-0000-0000-000000000000';
    
            if (isNewConversation) {
                // 🔥 FIX: Pass otherUserId to handleNewConversationSelection
                await this.handleNewConversationSelection(conversationId, otherUserId);
            } else {
                await this.handleExistingConversationSelection(conversationId);
            }
    
            console.log('=== selectConversation END ===');
        } catch (error) {
            console.error('❌ Error selecting conversation:', error);
            runInAction(() => {
                this.loadingMessages = false;
                this.isSelectingConversation = false;
            });
            throw error;
        }
    };

    private leavePreviousConversation = async () => {
        if (this.selectedConversation &&
            this.selectedConversation.id !== '00000000-0000-0000-0000-000000000000') {
            console.log('Leaving previous conversation:', this.selectedConversation.id);
            try {
                await this.safeHubInvoke('LeaveConversation', this.selectedConversation.id.toString());
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (error) {
                console.error('Error leaving conversation:', error);
            }
        }
    };

    private handleNewConversationSelection = async (conversationId: string, otherUserId?: string) => {
        console.log('📝 Selecting NEW conversation');
        console.log('Conversation ID:', conversationId);
        console.log('Other User ID:', otherUserId);
        
        // 🔥 FIX: For new conversations, find by otherUserId instead of conversationId
        const conversation = conversationId !== '00000000-0000-0000-0000-000000000000'
            ? this.conversations.find(c => c.id === conversationId)
            : this.conversations.find(c => c.otherUserId === otherUserId);
    
        if (conversation) {
            runInAction(() => {
                console.log('🔄 Setting selectedConversation for:', conversation.otherUserFullName);
                this.selectedConversation = {
                    id: conversation.id,
                    otherUserId: conversation.otherUserId,
                    otherUserFullName: conversation.otherUserFullName,
                    otherUserImage: conversation.otherUserImage,
                    otherUserUserName: conversation.otherUserUserName,
                    isOtherUserOnline: this.onlineUsers.has(conversation.otherUserId),
                    gameId: conversation.gameId,
                    gameTitle: conversation.gameTitle,
                    gameDate: conversation.gameDate
                };
                this.messages = [];
                this.loadingMessages = false;
                this.isSelectingConversation = false;
            });
        } else {
            console.error('❌ Conversation not found in list');
            runInAction(() => {
                this.loadingMessages = false;
                this.isSelectingConversation = false;
            });
        }
    };

    private handleExistingConversationSelection = async (conversationId: string) => {
        console.log('💬 Selecting EXISTING conversation:', conversationId);

        // Double-check connection before joining
        if (!this.hubConnection || this.hubConnection.state !== 'Connected') {
            throw new Error('Connection lost. Please try again.');
        }

        try {
            await this.safeHubInvoke('JoinConversation', conversationId);
            console.log('✅ Joined conversation group');
        } catch (error) {
            console.error('❌ Error joining conversation group:', error);
            
            // Attempt to recover connection before throwing error
            console.log('🔄 Attempting connection recovery...');
            await this.ensureConnectionReady();
            throw error;
        }

        console.log('📨 Loading conversation data...');
        const [messages, conversationDetail] = await Promise.all([
            agent.Messages.getMessages(conversationId),
            agent.Messages.getConversation(conversationId)
        ]);

        console.log('✅ API data loaded:', messages.length, 'messages');

        runInAction(() => {
            console.log('🔄 Setting selectedConversation');

            messages.forEach(m => {
                m.createdAt = new Date(m.createdAt);
                if (m.isSentByCurrentUser === undefined) {
                    const currentUserId = store.userStore.user?.userID;
                    m.isSentByCurrentUser = m.senderId === currentUserId;
                }
            });

            this.messages = messages;
            this.selectedConversation = {
                id: conversationDetail.id,
                otherUserId: conversationDetail.otherUserId,
                otherUserFullName: conversationDetail.otherUserFullName,
                otherUserImage: conversationDetail.otherUserImage,
                otherUserUserName: conversationDetail.otherUserUserName,
                isOtherUserOnline: this.onlineUsers.has(conversationDetail.otherUserId),
                gameId: conversationDetail.gameId,
                gameTitle: conversationDetail.gameTitle,
                gameDate: conversationDetail.gameDate
            };

            this.loadingMessages = false;
            this.isSelectingConversation = false;

            // Mark as read
            const conversation = this.conversations.find(c => c.id === conversationId);
            if (conversation) {
                conversation.unreadCount = 0;
            }
        });
    };

    // ========== MESSAGE OPERATIONS ==========
    sendMessage = async (content: string, recipientId?: string) => {
        if (!content.trim()) return;

        try {
            const isNewConversation = !this.selectedConversation?.id ||
                this.selectedConversation.id === '00000000-0000-0000-0000-000000000000';

            const command = {
                conversationId: this.selectedConversation?.id || "00000000-0000-0000-0000-000000000000",
                recipientId: isNewConversation
                    ? this.selectedConversation?.otherUserId
                    : recipientId,
                content: content.trim()
            };

            console.log('Sending message with command:', {
                conversationId: command.conversationId,
                recipientId: command.recipientId,
                isNewConversation
            });

            await this.safeHubInvoke('SendMessage', command);

        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    };

    // ========== CONVERSATION OPERATIONS ==========
    startNewConversation = async (recipientId: string, initialMessage?: string) => {
        try {
            const existingConversation = this.conversations.find(c => c.otherUserId === recipientId);

            if (existingConversation && existingConversation.id !== '00000000-0000-0000-0000-000000000000') {
                await this.selectConversation(existingConversation.id);
                if (initialMessage) {
                    await this.sendMessage(initialMessage);
                }
                return existingConversation.id;
            }

            const areFriends = await agent.Messages.checkFriendship(recipientId);

            if (!areFriends) {
                throw new Error('You can only message your friends. Send them a friend request first.');
            }

            if (initialMessage) {
                const command = {
                    conversationId: "00000000-0000-0000-0000-000000000000",
                    recipientId: recipientId,
                    content: initialMessage.trim()
                };

                await this.safeHubInvoke('SendMessage', command);
                await new Promise(resolve => setTimeout(resolve, 800));
                await this.refreshConversations();

                const newConversation = this.conversations.find(c =>
                    c.otherUserId === recipientId &&
                    c.id !== '00000000-0000-0000-0000-000000000000'
                );

                if (newConversation) {
                    await this.selectConversation(newConversation.id);
                    return newConversation.id;
                } else {
                    throw new Error('Failed to create conversation. Please try again.');
                }
            } else {
                const existingConv = this.conversations.find(c => c.otherUserId === recipientId);
                if (existingConv) {
                    runInAction(() => {
                        this.selectedConversation = {
                            id: existingConv.id,
                            otherUserId: existingConv.otherUserId,
                            otherUserFullName: existingConv.otherUserFullName,
                            otherUserImage: existingConv.otherUserImage,
                            otherUserUserName: existingConv.otherUserUserName,
                            isOtherUserOnline: this.onlineUsers.has(existingConv.otherUserId),
                            gameId: existingConv.gameId,
                            gameTitle: existingConv.gameTitle,
                            gameDate: existingConv.gameDate
                        };
                        this.messages = [];
                    });
                }
                return existingConv?.id || "00000000-0000-0000-0000-000000000000";
            }
        } catch (error) {
            console.error('Error starting new conversation:', error);
            throw error;
        }
    };

    refreshConversations = async () => {
        try {
            const conversations = await agent.Messages.getConversations();

            runInAction(() => {
                if (conversations) {
                    conversations.forEach((conv: ConversationDTO) => {
                        if (conv.lastMessageAt) {
                            conv.lastMessageAt = new Date(conv.lastMessageAt);
                        }
                        if (conv.gameDate) {
                            conv.gameDate = new Date(conv.gameDate);
                        }
                    });
                    this.conversations = conversations;
                }
            });
        } catch (error) {
            console.error('Error refreshing conversations:', error);
        }
    };

    searchConversations = async (searchTerm: string) => {
        try {
            const conversations = await agent.Messages.getConversations(searchTerm);

            runInAction(() => {
                if (conversations) {
                    conversations.forEach((conv: ConversationDTO) => {
                        if (conv.lastMessageAt) {
                            conv.lastMessageAt = new Date(conv.lastMessageAt);
                        }
                        if (conv.gameDate) {
                            conv.gameDate = new Date(conv.gameDate);
                        }
                    });
                    this.conversations = conversations;
                }
            });
        } catch (error) {
            console.error('Error searching conversations:', error);
        }
    };

    checkFriendship = async (userId: string): Promise<boolean> => {
        try {
            const areFriends = await agent.Messages.checkFriendship(userId);
            return areFriends === true;
        } catch (error) {
            console.error('Error checking friendship:', error);
            return false;
        }
    };

    // ========== DESKTOP MODE MANAGEMENT ==========
    joinRelevantConversations = async () => {
        if (!this.hubConnection || this.hubConnection.state !== 'Connected') {
            console.log('❌ Cannot join conversations - hub not connected');
            return;
        }
    
        if (this.hasJoinedDesktopGroups) {
            console.log('ℹ️ Already joined desktop conversation groups');
            return;
        }
    
        console.log('🔗 Joining relevant conversation groups for desktop');
        
        const recentConversations = this.conversations
            .filter(conv => conv.id && conv.id !== '00000000-0000-0000-0000-000000000000')
            .slice(0, 10);
        
        console.log(`Joining ${recentConversations.length} recent conversations`);
        
        let joinedCount = 0;
        for (const conversation of recentConversations) {
            try {
                await this.safeHubInvoke('JoinConversation', conversation.id);
                joinedCount++;
                console.log(`✅ Joined: ${conversation.otherUserFullName}`);
            } catch (error) {
                console.log(`⚠️ Could not join ${conversation.otherUserFullName}:`, (error as Error).message);
            }
        }
        
        this.hasJoinedDesktopGroups = joinedCount > 0;
        console.log(`✅ Desktop: Joined ${joinedCount} conversation groups`);
    };

    setupDesktopMode = async () => {
        console.log('🖥️ Setting up desktop mode (SignalR-driven, no polling)');
        
        // Check if we're actually in desktop mode
        if (typeof window === 'undefined' || window.innerWidth <= 768) {
            console.log('📱 Not in desktop mode, skipping');
            return;
        }
        
        // Initial data load
        await this.refreshConversations();
        
        // Join conversation groups to receive SignalR events
        await this.joinRelevantConversations();
        
        // Optional: Add a long fallback refresh (60s) as safety net only
        // This catches edge cases where SignalR events might be missed
        if (!this.desktopFallbackInterval) {
            this.desktopFallbackInterval = setInterval(() => {
                if (this.hubConnection?.state === 'Connected') {
                    console.log('🔄 Fallback refresh (60s safety net)');
                    this.refreshConversations();
                }
            }, 60000); // 60 seconds instead of 5
        }
        
        console.log('✅ Desktop mode setup complete - relying on SignalR push events');
    };

    cleanupDesktopMode = () => {
        console.log('🧹 Cleaning up desktop mode');
        
        // Remove old intervals
        if (this.desktopRefreshInterval) {
            clearInterval(this.desktopRefreshInterval);
            this.desktopRefreshInterval = null;
        }
        
        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = null;
        }
        
        // Remove new fallback interval
        if (this.desktopFallbackInterval) {
            clearInterval(this.desktopFallbackInterval);
            this.desktopFallbackInterval = null;
        }
        
        this.hasJoinedDesktopGroups = false;
    };

    checkAndSetupDesktopMode = () => {
        if (typeof window !== 'undefined' && window.innerWidth > 768) {
            console.log('💻 Desktop mode detected - setting up');
            setTimeout(() => {
                this.setupDesktopMode();
            }, 1000);
        }
    };

    checkAndUpdateDesktopMode = (isDesktop: boolean) => {
        console.log('🔄 Checking desktop mode:', isDesktop);
        
        if (isDesktop && this.hubConnection?.state === 'Connected') {
            console.log('🖥️ Setting up desktop mode');
            this.setupDesktopMode();
        } else if (!isDesktop) {
            console.log('📱 Cleaning up desktop mode');
            this.cleanupDesktopMode();
        }
    };

    // ========== PRIVATE HELPERS ==========
    private updateConversationInList = (message: MessageDTO) => {
        runInAction(() => {
            let index = this.conversations.findIndex(c => c.id === message.conversationId);
    
            if (index === -1 && message.conversationId !== '00000000-0000-0000-0000-000000000000') {
                const otherUserId = message.isSentByCurrentUser
                    ? message.recipientId
                    : message.senderId;
    
                if (otherUserId) {
                    index = this.conversations.findIndex(c =>
                        c.id === '00000000-0000-0000-0000-000000000000' &&
                        c.otherUserId === otherUserId
                    );
                }
    
                if (index !== -1) {
                    this.conversations[index].id = message.conversationId;
                }
            }
    
            if (index !== -1) {
                const conversation = this.conversations[index];
    
                conversation.lastMessage = message.content;
                conversation.lastMessageAt = message.createdAt;
                
                const updatedConversations = [...this.conversations];
                updatedConversations.splice(index, 1);
                updatedConversations.unshift(conversation);
                this.conversations = updatedConversations;
            } else {
                this.refreshConversations();
            }
        });
    };

    private markConversationAsRead = async (conversationId: string) => {
        try {
            runInAction(() => {
                const conversation = this.conversations.find(c => c.id === conversationId);
                if (conversation) {
                    conversation.unreadCount = 0;
                    this.conversations = [...this.conversations];
                }
            });
        } catch (error) {
            console.error('Error marking conversation as read:', error);
        }
    };

    updateConversationUnreadCount = (conversationId: string, increment: boolean = true) => {
        runInAction(() => {
            console.log('📊 updateConversationUnreadCount called:', {
                conversationId,
                increment,
                currentConversations: this.conversations.length
            });
            
            const index = this.conversations.findIndex(c => c.id === conversationId);
            if (index !== -1) {
                const conversation = this.conversations[index];
                console.log('📊 Found conversation:', {
                    name: conversation.otherUserFullName,
                    currentUnread: conversation.unreadCount
                });
                
                if (increment) {
                    conversation.unreadCount = (conversation.unreadCount || 0) + 1;
                } else {
                    conversation.unreadCount = 0;
                }
                
                console.log('📊 Updated unread count to:', conversation.unreadCount);
                
                const newConversations = [...this.conversations];
                newConversations[index] = { ...conversation };
                this.conversations = newConversations;
                
                console.log('📊 Conversations array updated');
            } else {
                console.log('❌ Conversation not found for unread update:', conversationId);
                this.refreshConversations();
            }
        });
    };

    // ========== CLEANUP ==========
    stopHubConnection = () => {
        console.log('🛑 Stopping hub connection');
        
        // Cancel any pending retry
        this.cancelPendingRetry();
        
        // Reset connection state
        this.isConnecting = false;
        this.connectionRetryCount = 0;
        
        // Stop online status refresh
        this.stopOnlineStatusRefresh();
        
        // Clean up desktop mode
        this.cleanupDesktopMode();
        
        // Clear presence interval
        if (this.presenceInterval) {
            clearInterval(this.presenceInterval);
            this.presenceInterval = null;
        }

        // Leave current conversation
        if (this.selectedConversation &&
            this.selectedConversation.id !== '00000000-0000-0000-0000-000000000000') {
            this.safeHubInvoke('LeaveConversation', this.selectedConversation.id.toString())
                .catch(err => console.error('Error leaving conversation:', err));
        }

        // Stop connection
        this.hubConnection?.stop()
            .then(() => console.log('✅ Hub connection stopped'))
            .catch(error => console.log('Error stopping connection: ', error));
    };

    clearMessages = () => {
        runInAction(() => {
            this.messages = [];
            this.selectedConversation = null;
        });
    };

    deleteMessage = async (messageId: string) => {
        try {
            await this.safeHubInvoke('DeleteMessage', messageId);
        } catch (error) {
            console.error('Error deleting message:', error);
            throw error;
        }
    }

    clearAll = () => {
        this.conversations = [];
        this.messages = [];
        this.selectedConversation = null;
        this.onlineUsers.clear();
        this.stopHubConnection();
    };

    // ========== UTILITY METHODS ==========
    isUserOnline = (userId: string): boolean => {
        return this.onlineUsers.has(userId);
    };

    // ========== CONNECTION HEALTH ==========
    checkConnectionHealth = () => {
        const state = this.hubConnection?.state;
        console.log('❤️ Connection health check:', {
            state,
            connectionId: this.hubConnection?.connectionId?.substring(0, 8),
            time: new Date().toISOString()
        });
        
        if (state !== 'Connected') {
            console.log('⚠️ Connection not healthy');
            this.attemptReconnect();
        }
    };

    attemptReconnect = async () => {
        if (this.hubConnection?.state === 'Disconnected') {
            console.log('🔄 Attempting manual reconnect...');
            try {
                await this.createHubConnection(true);
            } catch (error) {
                console.error('❌ Manual reconnect failed:', error);
            }
        }
    };
}

/*
 * DESKTOP MODE STRATEGY (POST-FIX):
 * 
 * 1. Join top 10 conversation groups on desktop → receive ALL SignalR events
 * 2. Trust SignalR event handlers to update state in real-time
 * 3. Optional 60s fallback refresh as safety net (can be removed if stable)
 * 4. NO aggressive polling - SignalR push events handle everything
 * 
 * Key insight: With conversation groups joined, desktop receives:
 * - ReceiveMessage events → updates messages + conversation list
 * - ConversationUpdated events → handles any changes
 * - UserOnlineStatusChanged → updates online indicators
 * 
 * The presence heartbeat (15s) keeps connection alive - no need for keepAlive.
 */